import React, { useEffect, useState } from 'react';

import { ButtonUI, DataGridIdentifiers, DatagridPremiumUi, DialogUI } from '../Interface';
import { dataGridPremiumColumns } from '../../utils/helper';
import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { DocumentType } from '../../config/DocumentType';
import { PriceFormField } from '../../config/priceList';
import { multiply, round } from 'mathjs';

const DOWN_PAYMENT_DOCUMENT_TYPES = [DocumentType.DownPayment, DocumentType.APDownPayment];

const toAbsoluteAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.abs(amount) : 0;
};

const normalizeAmount = (value) => Number(Number(value).toFixed(10));

const getRemainingGrossAmount = (row) =>
  normalizeAmount(Math.max(toAbsoluteAmount(row?.grossPrice) - toAbsoluteAmount(row?.appliedGrossAmount), 0));

const DownPaymentTableDialog = (props) => {
  const { values, setFieldValue } = useFormikContext();
  const { open, onClose, data, defaultDocumentLine, ownCurrency } = props;
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  useEffect(() => {
    if (!open) setRowSelectionModel([]);
  }, [open]);

  const importedSourceLineKeys = new Set(
    (values.docLine ?? [])
      .filter((line) => DOWN_PAYMENT_DOCUMENT_TYPES.includes(Number(line?.baseDocType)))
      .map((line) => `${line?.baseLine}_${line?.baseId}`)
  );
  const availableRows = (data ?? []).filter(
    (row) =>
      getRemainingGrossAmount(row) > 0 &&
      (!values.currencyCode || row?.doc?.currencyCode === values.currencyCode) &&
      !importedSourceLineKeys.has(`${row?.lineNum}_${row?.doc?.docId}`)
  );

  const getNetCapacityByVatCode = () =>
    (values.docLine ?? []).reduce((capacities, line) => {
      const vatCode = line?.vatCode?.trim() ?? '';
      const current = capacities.get(vatCode) ?? 0;
      const netPrice = Number(line?.netPrice);
      const normalizedNetPrice = Number.isFinite(netPrice) ? netPrice : 0;
      const next = DOWN_PAYMENT_DOCUMENT_TYPES.includes(Number(line?.baseDocType))
        ? current - Math.abs(normalizedNetPrice)
        : current + normalizedNetPrice;
      capacities.set(vatCode, next);
      return capacities;
    }, new Map());

  const columns = dataGridPremiumColumns([
    {
      field: 'docNum',
      headerName: 'Előlegszámla száma',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.doc?.docNum;
      },
    },
    {
      field: 'note',
      headerName: 'Megjegyzés',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.doc?.note;
      },
    },
    {
      field: 'currencyCode',
      headerName: 'Előleg pénzneme',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        return data.row.doc?.currencyCode;
      },
    },
    {
      field: 'itemName',
      headerName: 'Cikk megnevezés',
      type: 'string',
      filterable: false,
    },
    {
      field: 'vatCode',
      headerName: 'Áfa kód',
      type: 'string',
      filterable: false,
    },
    {
      field: 'netPrice',
      headerName: 'Nettó érték',
      type: 'number',
      filterable: false,
    },
    {
      field: 'vatValue',
      headerName: 'Áfa érték',
      type: 'number',
      filterable: false,
    },
    {
      field: 'grossPrice',
      headerName: 'Bruttó érték',
      type: 'number',
      filterable: false,
    },
    {
      field: 'appliedGrossAmount',
      headerName: 'Felhasznált bruttó',
      type: 'number',
      filterable: false,
    },
    {
      field: 'remainingGrossAmount',
      headerName: 'Felhasználható bruttó',
      type: 'number',
      filterable: false,
      valueGetter: (_value, row) => getRemainingGrossAmount(row),
    },
  ]);

  const handleSelectRows = () => {
    const nextVisOrder =
      (values.docLine ?? []).reduce((maxOrder, line, index) => {
        const visOrder = line?.visOrder !== null && line?.visOrder !== undefined ? Number(line.visOrder) : NaN;
        const lineNum = line?.lineNum !== null && line?.lineNum !== undefined ? Number(line.lineNum) : NaN;
        const currentOrder = Number.isFinite(visOrder)
          ? visOrder
          : Number.isFinite(lineNum)
            ? lineNum
            : index;
        return Math.max(maxOrder, currentOrder);
      }, 0) + 1;

    const netCapacityByVatCode = getNetCapacityByVatCode();
    const selectedRows = availableRows
      .filter((row) => rowSelectionModel.includes(`${row.lineNum}_${row.doc.docId}`))
      .reduce((result, row) => {
        const { doc, ...rest } = row;
        const vatCode = row?.vatCode?.trim() ?? '';
        const originalGrossAmount = toAbsoluteAmount(row.grossPrice);
        const remainingGrossAmount = getRemainingGrossAmount(row);
        const remainingRatio = originalGrossAmount > 0 ? remainingGrossAmount / originalGrossAmount : 0;
        const remainingNetAmount = normalizeAmount(toAbsoluteAmount(row.netPrice) * remainingRatio);
        const netCapacity = Math.max(netCapacityByVatCode.get(vatCode) ?? 0, 0);
        const appliedNetAmount = Math.min(remainingNetAmount, netCapacity);
        const appliedRemainingRatio = remainingNetAmount > 0 ? appliedNetAmount / remainingNetAmount : 0;
        const appliedGrossAmount = normalizeAmount(remainingGrossAmount * appliedRemainingRatio);
        const appliedRatio = originalGrossAmount > 0 ? appliedGrossAmount / originalGrossAmount : 0;

        if (appliedRatio <= 0) return result;

        const exchangeRate = Number(values.exchangeRate);
        const toOwnCurrency = (value) =>
          round(multiply(value, exchangeRate > 0 ? exchangeRate : 1), ownCurrency?.amountRounding ?? 0);
        const quantity = toAbsoluteAmount(row.qty) || 1;
        const index = result.length;

        const normalized = {
          ...defaultDocumentLine,
          ...rest,
          id: uuidv4(),
          isNew: true,
          docId: undefined,
          lineNum: undefined,
          visOrder: nextVisOrder + index,
          dueDate: row.dueDate ? dayjs(row.dueDate) : defaultDocumentLine.dueDate,
          qty: -quantity,
          unitPrice: normalizeAmount(appliedNetAmount / quantity),
          grossUnitPrice: normalizeAmount(appliedGrossAmount / quantity),
          priceUnitPrice: normalizeAmount(appliedNetAmount / quantity),
          priceCurrencyCode: values.currencyCode,
          priceExchangeRate: 1,
          priceForm: PriceFormField.NET,
          baseUnitPrice: normalizeAmount(appliedNetAmount / quantity),
          baseGrossUnitPrice: normalizeAmount(appliedGrossAmount / quantity),
          netPrice: -normalizeAmount(appliedNetAmount),
          vatValue: -normalizeAmount(appliedGrossAmount - appliedNetAmount),
          grossPrice: -appliedGrossAmount,
          netPriceInOwnCur: toOwnCurrency(-normalizeAmount(appliedNetAmount)),
          vatValueInOwnCur: toOwnCurrency(-normalizeAmount(appliedGrossAmount - appliedNetAmount)),
          grossPriceInOwnCur: toOwnCurrency(-appliedGrossAmount),
          appliedGrossAmount: 0,
          baseId: doc?.docId ?? null,
          baseDocnum: doc?.docNum ?? null,
          baseDocType: doc?.docTypeId ?? null,
          baseLine: row.lineNum,
        };

        netCapacityByVatCode.set(vatCode, normalizeAmount(netCapacity - appliedNetAmount));
        result.push(normalized);
        return result;
      }, []);

    if (selectedRows.length === 0) {
      addNotification({
        content: 'A kiválasztott előleghez nincs felhasználható nettó összeg ezen az áfakódon.',
        type: NotificationType.ERROR,
      });
      return;
    }

    setFieldValue('docLine', [...(values.docLine ?? []), ...selectedRows]);
    onClose();
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent="Előleg" maxWidth="xl" fullWidth>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.DownPaymentDialog}
        datagridInitialState={{ density: 'compact' }}
        rows={availableRows}
        hideFooter
        height={700}
        columns={columns}
        headerFilters={false}
        getRowId={(row) => `${row.lineNum}_${row.doc.docId}`}
        paginationAndfilterMode="client"
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newModel) => setRowSelectionModel(newModel)}
      />

      <Box display="flex" justifyContent="center" gap="0.5rem" mt="1rem">
        <ButtonUI
          text="Előleg beolvasása"
          className="bg-success"
          onClick={handleSelectRows}
          disabled={rowSelectionModel?.length === 0}
        />
        <ButtonUI text="Mégsem" className="bg-primary" onClick={onClose} />
      </Box>
    </DialogUI>
  );
};

export default DownPaymentTableDialog;
